<script setup lang="js">
import AuthedLayout from '@/layouts/AuthedLayout.vue'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import { computed, onBeforeMount, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { FileStatus } from 'filepond'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { dataSubmissionApi } from '@/api/DataSubmission/DataSubmissionApi.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import ComingSoonPanel from '@/components/Misc/ComingSoonPanel.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'
import { echoes } from '@/composables/echo/useEchoes.js'

const authStore = useAuthStore()
const tenant = Tenants.getTenant()
const toaster = new Toaster()

const FilePond = vueFilePond(FilePondPluginFileValidateType)
const serverConfig = {
	url: import.meta.env.VITE_API_URL + '/data-submission',
	process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
		const formData = new FormData()
		formData.append(fieldName, file, file.name)
		formData.append('user_id', authStore.getAuth.user().id)
		formData.append('tenant_id', authStore.getAuth.user().tenant_id)
		formData.append('tenant_name', authStore.getAuth.tenantInfo().name)
		formData.append('organization_id', authStore.activeOrganizationId)
		formData.append('organization_name', authStore.getActiveOrganization.organization_name)

		const request = new XMLHttpRequest();
		request.open('POST', import.meta.env.VITE_API_URL + '/data-submission/upload')
		request.setRequestHeader('X-CSRF-TOKEN', window.csrf_token)
		request.setRequestHeader('Authorization', `Bearer ${useAuthStore().rawJwt}`)
		request.setRequestHeader('x-client-user-id', authStore.getAuth?.user()?.id)
		request.setRequestHeader('x-jwt-version-code', authStore.getAuth?.user()?.jwt_version_code)
		request.setRequestHeader('X-Socket-ID', echoes.socketId());

		request.upload.onprogress = (e) => {
			progress(e.lengthComputable, e.loaded, e.total)
		}

		request.onload = function () {
			if (request.status >= 200 && request.status < 300) {
				load(request.responseText)
			}
			else
			{
				const data = JSON.parse(request.response)
				if (data['errors'] && data['errors'].length > 0)
				{
					uploadErrors.value = [...uploadErrors.value, ...data['errors']];
				}

				console.log('err', data)
				error('File upload failed.')
			}
		}

		request.onerror = () => {
			error('File upload failed.');
		};

		request.send(formData)

		return {
			abort: () => {
				request.abort()
				abort()
			},
		};
	},
}

const filesQueued = ref([])
const filesBeingProcessed = ref([])
const uploadProgress = ref(new Map())
const isUploading = ref(false)
const showOrganizationPrompt = ref(false)
const maxConcurrentUploads = Number(import.meta.env.VITE_DATA_SUBMISSION_CONCURRENT_UPLOAD_LIMIT ?? 5)
const uploadErrors = ref([])
const submit_warning_text = ref("Do not include PHI or PII in file names.")

let labelIdle = `onBeforeMount`

onBeforeMount(() => {
	submit_warning_text.value = JSON.parse(authStore.getAuth.tenantInfo().settings_json)?.data_submissions?.submit_warning_text?.value

	labelIdle = `
		<svg class="upload-icon mx-auto mb-4" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
			<title>Upload</title>
			<desc>Upload files here</desc>
			<rect x="0.5" y="0.5" width="47" height="47" rx="11.5" fill="white"/>
			<rect x="0.5" y="0.5" width="47" height="47" rx="11.5" stroke="#C3C6D5"/>
			<path d="M33.0375 16.1625H24.375L23.5875 14.6625C23.175 13.9125 22.425 13.425 21.5625 13.425H14.9625C13.6875 13.425 12.675 14.4375 12.675 15.7125V32.2875C12.675 33.5625 13.6875 34.5751 14.9625 34.5751H33.075C34.35 34.5751 35.3625 33.5625 35.3625 32.2875V18.45C35.3625 17.175 34.3125 16.1625 33.0375 16.1625ZM33.675 32.2875C33.675 32.625 33.4125 32.8876 33.075 32.8876H14.9625C14.625 32.8876 14.3625 32.625 14.3625 32.2875V15.7125C14.3625 15.375 14.625 15.1125 14.9625 15.1125H21.5625C21.7875 15.1125 21.975 15.225 22.0875 15.45L23.1375 17.4C23.2875 17.6625 23.5875 17.85 23.8875 17.85H33.075C33.4125 17.85 33.675 18.1125 33.675 18.45V32.2875Z" fill="#0F191C"/>
			<path d="M24.6 21.1501C24.2625 20.8126 23.7375 20.8126 23.4 21.1501L20.1375 24.3751C19.8 24.7126 19.8 25.2376 20.1375 25.5751C20.475 25.9126 21 25.9126 21.3375 25.5751L23.175 23.7751V29.2501C23.175 29.7001 23.55 30.1126 24.0375 30.1126C24.525 30.1126 24.8625 29.7376 24.8625 29.2501V23.7376L26.7375 25.5751C26.8875 25.7251 27.1125 25.8001 27.3375 25.8001C27.5625 25.8001 27.7875 25.7251 27.9375 25.5376C28.275 25.2001 28.275 24.6751 27.9375 24.3376L24.6 21.1501Z" fill="#0F191C"/>
		</svg>

		<div class="flex flex-row m-auto w-full justify-center">
			<div>
				Drag and drop files here, or
			</div>
			<p class='clickable-text'>&nbsp;browse</p>
		</div>

		<div class="text-error-on-color text-sm mt-2 whitespace-pre-wrap">${ submit_warning_text.value }</div>
	`;

})

const progressBar = computed(() => {
	let totalProgress = 0
	uploadProgress.value.forEach((progress, id) => {
		totalProgress += progress
	})
	totalProgress = Math.round(totalProgress * 100 / filesBeingProcessed.value.length)
	const prefix = totalProgress >= 100 ? 'Finishing up...' : 'Uploading...'
	totalProgress = `width: ${totalProgress}%;`

	return `${prefix}<p><div class="relative inset-0 justify-center items-center w-full mt-2"><div class="absolute rounded-full w-full h-2 border z-0"></div><div class="absolute rounded-full h-2 bg-primary z-10" style="${totalProgress}"></div></div>`
})

const uploadEnabled = computed(() => {
	return isUploading.value === false && filesQueued.value != null && filesQueued.value.length > 0
})

const uploadPromptMessage = computed(() => {
	let organizationName = ''

	authStore?.userOrganizationData?.organizations?.forEach((organization) => {
		if (organization.id === authStore.activeOrganizationId) {
			organizationName = organization.organization_name
		}
	})

	return 'The ' + (filesQueued.value.length > 0 ? 'files' : 'file') + ' you are about to upload will be uploaded under the <b>' + organizationName + '</b> organization.<br><br>If this is incorrect, you can change your active organization by clicking on your <b>initials</b> in the top right and expanding <b>My Organizations</b>.'
})

const pond = ref(null)

function onFileAdded(error, file) {
	const outerContainer = document.getElementById('filepond--item-' + file.id)
	let infoMain = outerContainer.querySelector('.filepond--file-info-main')

	if (infoMain) {
		const wrapper = document.createElement('div')
		wrapper.classList.add('flex', 'space-x-2', 'filepond--file-info-main', 'w-full')
		infoMain.parentNode.insertBefore(wrapper, infoMain)
		infoMain.classList.remove('filepond--file-info-main')
		wrapper.appendChild(infoMain)

		const spinnerId = 'file-exists-spinner-' + file.id
		const warningId = 'file-exists-warning-' + file.id
		const fileExistsWrapper = document.createElement('div')
		fileExistsWrapper.classList.add('truncate')
		fileExistsWrapper.innerHTML = `
			<svg id="` + spinnerId + `" role="img" aria-labelledby="spinnerTitle" class="pointer-events-auto animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
				<title id="spinnerTitle">Checking for file name conflict...</title>
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span id="` + warningId + `" class="hidden text-error pointer-events-auto" style="font-size: 14px;" title="A file with this name already exists. After uploading, please provide a description to avoid confusion.">A file with this name already exists. After uploading, please provide a description to avoid confusion.</span>
		`
		wrapper.appendChild(fileExistsWrapper)

		dataSubmissionApi.fileExists(file.filename, authStore.activeOrganizationId, (exists) => {
			document.getElementById(spinnerId).classList.add("hidden")

			if (exists) {
				document.getElementById(warningId).classList.remove("hidden")
			}
		})
	}
}

function onUpdateFiles(files) {
	filesBeingProcessed.value = []

	// Files about to begin processing
	filesBeingProcessed.value = []
	let filtered = files.filter((file) => {
		return file.status === FileStatus.PROCESSING_QUEUED
	})

	filtered.forEach((file) => {
		filesBeingProcessed.value.push(file)
	})

	// Files queued waiting for processing to start
	filesQueued.value = []
	filtered = files.filter((file) => {
		return file.status === FileStatus.IDLE
	})

	filtered.forEach((file) => {
		filesQueued.value.push(file)
	})

	isUploading.value = filesBeingProcessed.value.length > 0
}

function onFileProcessed() {
	const statusOrder = [FileStatus.IDLE, FileStatus.LOAD_ERROR, FileStatus.LOADING, FileStatus.PROCESSING_QUEUED, FileStatus.PROCESSING, FileStatus.PROCESSING_ERROR, FileStatus.PROCESSING_COMPLETE]

	pond.value.sort((a, b) => {
		const aIndex = statusOrder.indexOf(a.status)
		const bIndex = statusOrder.indexOf(b.status)

		if (aIndex > bIndex) {
			// a to bottom
			return 1;
		}
		else if (aIndex < bIndex) {
			// a to top
			return -1;
		}

		return 0;
	})

	if (isProcessingComplete()) {
		onFilesProcessed()//Do we need this to be called here?
		handleErrors()
	}
}

function handleErrors()
{
	try
	{
		if (filesBeingProcessed.value.length === 0)
		{
			let error_messages = []
			for (let i = 0; i < uploadErrors.value.length; i++)
			{
				//Just capture permission error for now.
				if (uploadErrors.value[i].status === 403) error_messages.push(uploadErrors.value[i].detail)
			}
			error_messages = [...new Set(error_messages)];
			if (error_messages.length > 0)
			{
				toaster.error(error_messages.join("<br/>"))
			}
			uploadErrors.value = []
		}
	}
	catch (e){ /* empty */ }

}

/**
 * This is redundant in general, but in the case of a file that failed to upload retrying doesn't trigger onUpdateFiles()
 */
function onFileProcessStart(file) {
	const fileAlreadyThere = filesBeingProcessed.value.find((item) => {
		return item.id === file.id
	})

	if (!fileAlreadyThere) {
		filesBeingProcessed.value.push(file)
		isUploading.value = true
	}
}

function onFilesProcessed() {
	filesBeingProcessed.value = []
	filesQueued.value = []
	uploadProgress.value = new Map()
	isUploading.value = false

	UserpilotService.track('data_submissions_upload', {
		"user_id": authStore.getAuth.user().id,
		"organization_id": authStore.activeOrganizationId
	});

}

function onProcessFileProgress(file, progress) {
	uploadProgress.value.set(file.id, progress)
}

function onError() {
	if (isProcessingComplete()) {
		onFilesProcessed()
	}
}

/**
 * If one or more of the files have an error when uploading, the onFilesProcessed() function does not get called and isUploading doesn't get updated to true
 */
function isProcessingComplete() {
	const nonErrorStatuses = [FileStatus.IDLE, FileStatus.LOADING, FileStatus.PROCESSING_QUEUED, FileStatus.PROCESSING, FileStatus.PROCESSING_COMPLETE]

	const filesProcessingOrComplete = filesBeingProcessed.value.filter(file => {
		return file.status in nonErrorStatuses
	})

	return filesProcessingOrComplete.length === 0
}

function beginUpload() {
	showOrganizationPrompt.value = false
	pond.value.processFiles()
}

function hideOrganizationPrompt() {
	showOrganizationPrompt.value = false
}

</script>

<template>
	<AuthedLayout>
		<p class="dashboard-title">Submit Data</p>
		<div class="flex flex-col p-8 rounded-xl bg-neutral border border-neutral-subtle card-shadow">
			<div v-if="tenant.isRouteComingSoon('data.submit-data')">
				<ComingSoonPanel message="Data Submission is currently under construction and will be available to you soon." />
			</div>
			<div v-else>
				<p class="admin-center-subtitle">New Submission</p>
				<div class="text-grayscale-body leading-6 mt-1">
					Add your files below, then continue to <router-link :to="{ name: 'data.data-submissions' }" class="clickable-text">{{ (authStore.isAnyAdmin || authStore.canViewAllOrgFiles) ? "Data Submissions" : "My Submissions" }}</router-link>&nbsp;to briefly describe the reason for the submissions or resubmission - this helps us route and review your submission faster.
				</div>

				<p class="text-heading mt-6 font-semibold">Files</p>

				<div class="mt-3">
					<file-pond
						name="file"
						ref="pond"
						:label-idle="isUploading ? progressBar : labelIdle"
						:allow-multiple="true"
						:server="serverConfig"
						:allow-process="true"
						:allow-revert="false"
						:allow-remove="true"
						:instant-upload="false"
						:max-parallel-uploads="maxConcurrentUploads"
						@addfile="onFileAdded"
						@processfilestart="onFileProcessStart"
						@processfile="onFileProcessed"
						@processfiles="onFilesProcessed"
						@updatefiles="onUpdateFiles"
						@processfileprogress="onProcessFileProgress"
						@error="onError"
					/>
				</div>

				<div class="flex w-full flex-col-reverse sm:flex-row sm:gap-4 mt-2 gap-0">
					<div class="sm:mr-auto mt-1 sm:mt-0"></div><!-- remove if showing Deactivate -->
					<SubmitButton label="Cancel" :type="ButtonType.Outline" class="mt-3 sm:mt-0" :disabled="!uploadEnabled" :loading="isUploading" @click="pond.removeFiles()" />
					<SubmitButton label="Submit" :disabled="!uploadEnabled" :loading="isUploading" @click="showOrganizationPrompt = true" />
				</div>

			</div>
		</div>

		<Teleport to="html">
			<MultiButtonModal
				:show="showOrganizationPrompt"
				icon="info"
				title="Upload"
				:message="uploadPromptMessage"
				:buttons="[
									new ModalButtonData(
										'Cancel', hideOrganizationPrompt, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Continue', () => { beginUpload() }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>
	</AuthedLayout>
</template>
